import { useCallback } from 'react';
import { useNostr } from '@nostrify/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';

import { useNostrPublish } from './useNostrPublish';
import {
  FORUM_KIND,
  buildTree,
  parseNode,
  type ForumNode,
  type ForumNodeType,
  type ForumTree,
} from '@/lib/forum';

const FETCH_LIMIT = 2000;

/**
 * Merge freshly fetched relay events with the previous cache. We keep the
 * version of each node with the higher `created_at`, so:
 *  - newly published nodes (not yet on the relay) survive the refetch, and
 *  - recent edits (re-published with the same `d` but a newer timestamp)
 *    are not clobbered by the relay's stale copy.
 */
function mergeTree(fresh: ForumTree, prev?: ForumTree): ForumTree {
  if (!prev) return fresh;
  const byId = new Map(fresh.byId);
  for (const [id, prevNode] of prev.byId) {
    const freshNode = byId.get(id);
    if (!freshNode || prevNode.createdAt >= freshNode.createdAt) {
      byId.set(id, prevNode);
    }
  }
  return { nodes: [...byId.values()], byId };
}

/** Fetch every forum node (all kind 32323 events) and build the tree. */
export function useForumNodes() {
  const { nostr } = useNostr();

  return useQuery<ForumTree>({
    queryKey: ['forum', 'nodes'],
    queryFn: async ({ signal, client, queryKey }) => {
      const events = (await nostr.query(
        [{ kinds: [FORUM_KIND], limit: FETCH_LIMIT }],
        { signal },
      )) as NostrEvent[];
      const fresh = buildTree(events);
      const prev = client.getQueryData<ForumTree>(queryKey);
      return mergeTree(fresh, prev);
    },
    staleTime: 30_000,
  });
}

interface PublishParams {
  id: string;
  parent: string | null;
  title: string;
  content: string;
  type: ForumNodeType;
}

/** Insert a freshly-published event into the cached tree (optimistic UI). */
function insertNode(tree: ForumTree, event: NostrEvent): ForumTree {
  const node = parseNode(event);
  if (!node.id) return tree;
  const byId = new Map(tree.byId);
  byId.set(node.id, node);
  return { nodes: [...byId.values()], byId };
}

/**
 * Returns a `publishNode` function. It signs and broadcasts a kind 32323
 * event, then immediately inserts it into the cached forum tree so the UI
 * updates without waiting for relay propagation.
 */
export function useForumPublish() {
  const { mutateAsync } = useNostrPublish();
  const queryClient = useQueryClient();

  const publishNode = useCallback(
    async (params: PublishParams) => {
      const tags: string[][] = [
        ['d', params.id],
        ['alt', `NostrBoard ${params.type}`],
      ];
      if (params.parent) tags.push(['i', params.parent]);
      if (params.title) tags.push(['t', params.title]);

      const event = await mutateAsync({
        kind: FORUM_KIND,
        content: params.content,
        tags,
      });

      // Optimistically show the new node right away.
      queryClient.setQueryData<ForumTree>(['forum', 'nodes'], (old) =>
        old ? insertNode(old, event) : old,
      );

      // Refetch in the background so other users' posts show up too. The
      // query's merge step keeps this node visible until relays catch up.
      await queryClient.invalidateQueries({ queryKey: ['forum', 'nodes'] });

      return event;
    },
    [mutateAsync, queryClient],
  );

  /**
   * Edit an existing node by republishing it with the same `d` tag. Because
   * kind 32323 is addressable, relays keep only the latest version per
   * (pubkey, d) — so this cleanly updates the node in place. Only the
   * original author can edit (the `d` tag is per-pubkey).
   */
  const editNode = useCallback(
    async (node: ForumNode, title: string, content: string) => {
      const alt =
        node.event.tags.find((t) => t[0] === 'alt')?.[1] ?? 'NostrBoard edit';

      const tags: string[][] = [
        ['d', node.id],
        ['alt', alt],
      ];
      if (node.parent) tags.push(['i', node.parent]);
      if (title) tags.push(['t', title]);

      const event = await mutateAsync({
        kind: FORUM_KIND,
        content,
        tags,
      });

      // Optimistically replace the node in the cached tree.
      queryClient.setQueryData<ForumTree>(['forum', 'nodes'], (old) =>
        old ? insertNode(old, event) : old,
      );

      await queryClient.invalidateQueries({ queryKey: ['forum', 'nodes'] });

      return event;
    },
    [mutateAsync, queryClient],
  );

  return { publishNode, editNode };
}
